import React, { useCallback, useEffect, Dispatch } from "react";

import "./TestFlightInstallationFlow.scss";
import FilledButton, {
  ButtonSizes,
} from "../dashboard-page/FilledButton/FilledButton";
import { LINKS } from "../../utils/constants";
import CustomCheckbox from "../CustomCheckbox/CustomCheckbox";
import Bonus from "../Bonus/Bonus";
import DontForgetStepTwoModal from "../modals/DontForgetStepTwoModal/DontForgetStepTwoModal";
import useDisclosure from "../../hooks/useDisclosure";
import { ThemeVariant } from "../../utils/constants/generic-types";

interface TestflightInstallFlowProps {
  setHasCompletedAllSteps?: (completed: boolean) => void;
  hasCompletedAllSteps?: boolean;
  isLightMode?: boolean;
  setHasInstalledTestflight: Dispatch<React.SetStateAction<boolean>>;
  hasInstalledTestflight: boolean;
}

function TestFlightInstallationFlow({
  setHasCompletedAllSteps,
  hasCompletedAllSteps,
  isLightMode,
  hasInstalledTestflight,
  setHasInstalledTestflight,
}: TestflightInstallFlowProps) {
  const {
    isOpen: isDontForgetModalOpen,
    onClose: closeDontForgetModal,
    onOpen: openDontForgetModal,
  } = useDisclosure();

  const toggleInstalledStatus = useCallback(
    () => setHasInstalledTestflight((prev) => !prev),
    [setHasInstalledTestflight]
  );

  const handleInstallApp = useCallback(() => {
    window.open(
      LINKS.WEBPAGE.TEST_FLIGHT_JOIN,
      "_blank",
      "noopener,noreferrer"
    );
    setHasCompletedAllSteps && setHasCompletedAllSteps(true);
  }, [setHasCompletedAllSteps]);

  useEffect(() => {
    if (!hasInstalledTestflight && setHasCompletedAllSteps)
      setHasCompletedAllSteps(false);
  }, [hasInstalledTestflight, setHasCompletedAllSteps]);

  return (
    <div
      className="TestFlightInstallFlow p-3"
      style={{ background: isLightMode ? undefined : "#0e0529" }}
    >
      <h3
        className="heading"
        style={{ color: isLightMode ? undefined : "white" }}
      >
        {hasInstalledTestflight
          ? "Install Hyper gaming app."
          : "Get TestFlight"}
      </h3>
      <div
        className="content mb-5"
        style={{ color: isLightMode ? undefined : "white" }}
      >
        {!hasInstalledTestflight && (
          <p className="mb-3 mt-2">You need Testflight to install Hyper app.</p>
        )}
        <p>
          {hasInstalledTestflight
            ? "Hyper is only available via the button below. Only tap it if you have completed step 1."
            : "After installing Testflight, come back & tap “I have installed TestFlight”"}
        </p>
      </div>
      {hasInstalledTestflight ? (
        <div className="install-btn-container position-relative">
          <FilledButton
            style={{ fontWeight: 500 }}
            onClick={handleInstallApp}
            fullwidth
            size={ButtonSizes.MD}
          >
            Install Hyper Gaming App
          </FilledButton>
          <Bonus style={{ top: -9 }} />
        </div>
      ) : (
        <FilledButton
          fullwidth
          size={ButtonSizes.MD}
          onClick={openDontForgetModal}
        >
          Install TestFlight
        </FilledButton>
      )}
      <div className="d-flex align-items-center justify-content-center gap-2 pb-4 px-0 mt-3">
        {!hasInstalledTestflight && (
          <CustomCheckbox
            onChange={toggleInstalledStatus}
            checked={hasInstalledTestflight}
            style={{
              borderColor: isLightMode ? "#1F1F1F" : undefined,
              background:
                hasInstalledTestflight && isLightMode ? "#1F1F1F99" : undefined,
            }}
          />
        )}
        <span
          onClick={toggleInstalledStatus}
          className="text-sm text-start"
          style={{
            color: isLightMode ? undefined : "white",
            letterSpacing: -0.4,
          }}
        >
          {hasInstalledTestflight
            ? "Go back to step 1"
            : "I have installed TestFlight"}
        </span>
      </div>
      <DontForgetStepTwoModal
        show={isDontForgetModalOpen}
        onHide={closeDontForgetModal}
        onClickCta={closeDontForgetModal}
        theme={isLightMode ? ThemeVariant.LIGHT : ThemeVariant.DARK}
      />
    </div>
  );
}

export default TestFlightInstallationFlow;
